// src/pages/Admin/Settings/components/VisitesDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  FaTrash,
  FaTimes,
  FaSyncAlt,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../services/visitesServices";

const TRANSITION_MS = 300;

// Parse local (évite le décalage UTC du format YYYY-MM-DD)
function parseDateOnlyLocal(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return null;
  const [y, m, d] = isoDate.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function toISODateLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function sum(arr, key) {
  return arr.reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0);
}

function diffDaysInclusive(start, end) {
  const s = parseDateOnlyLocal(start);
  const e = parseDateOnlyLocal(end);
  if (!s || !e) return 0;
  const ms = e.getTime() - s.getTime();
  return Math.floor(ms / 86400000) + 1;
}

function fmtShort(iso) {
  const d = parseDateOnlyLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
  });
}

function fmtLong(iso) {
  const d = parseDateOnlyLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function buildMovingAvg(data, windowSize = 7) {
  const out = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - (windowSize - 1));
    let s = 0;
    for (let k = start; k <= i; k++) {
      s += Number(data[k]?.compteur) || 0;
    }
    const denom = i - start + 1;
    out.push({
      ...data[i],
      avg7: denom ? s / denom : 0,
    });
  }
  return out;
}

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function VisitesDashboard() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filtres
  const [rangeKey, setRangeKey] = useState("30d");
  const [startISO, setStartISO] = useState("");
  const [endISO, setEndISO] = useState("");

  // Slide-over nettoyage
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [cleanStart, setCleanStart] = useState("");
  const [cleanEnd, setCleanEnd] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [cleanErr, setCleanErr] = useState("");

  const fetchVisites = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await countVisitesByDate();
      setRaw(Array.isArray(res) ? res : []);
    } catch (e) {
      setErr("Impossible de récupérer les visites.");
      setRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisites();
  }, []);

  const dataAll = useMemo(() => {
    return (Array.isArray(raw) ? raw : [])
      .map((x) => ({
        date_visite: x.date_visite,
        compteur: Number(x.compteur) || 0,
        ts: parseDateOnlyLocal(x.date_visite)?.getTime() ?? 0,
      }))
      .filter((x) => x.ts > 0)
      .sort((a, b) => a.ts - b.ts);
  }, [raw]);

  useEffect(() => {
    const todayISO = toISODateLocal(new Date());

    if (!dataAll.length) {
      setStartISO(todayISO);
      setEndISO(todayISO);
      setRangeKey("30d");
      return;
    }

    const last = dataAll[dataAll.length - 1];
    const end = last?.date_visite || todayISO;
    const endD = parseDateOnlyLocal(end) || new Date();
    const startD = new Date(endD);
    startD.setDate(startD.getDate() - 29);

    setStartISO(toISODateLocal(startD));
    setEndISO(toISODateLocal(endD));
    setRangeKey("30d");
  }, [dataAll.length]);

  const applyQuickRange = (key) => {
    const todayISO = toISODateLocal(new Date());
    const endBaseISO =
      dataAll.length > 0
        ? dataAll[dataAll.length - 1].date_visite
        : todayISO;

    const endD = parseDateOnlyLocal(endBaseISO) || new Date();
    const startD = new Date(endD);

    if (key === "7d") startD.setDate(startD.getDate() - 6);
    else if (key === "30d") startD.setDate(startD.getDate() - 29);
    else if (key === "90d") startD.setDate(startD.getDate() - 89);
    else if (key === "1y") startD.setFullYear(startD.getFullYear() - 1);
    else if (key === "all") {
      const first = dataAll[0]?.date_visite;
      if (first) {
        setStartISO(first);
        setEndISO(endBaseISO);
        setRangeKey("all");
        return;
      }
    }

    setStartISO(toISODateLocal(startD));
    setEndISO(toISODateLocal(endD));
    setRangeKey(key);
  };

  const filtered = useMemo(() => {
    if (!startISO || !endISO) return dataAll;

    const s = parseDateOnlyLocal(startISO)?.getTime() ?? 0;
    const e = parseDateOnlyLocal(endISO)?.getTime() ?? 0;
    if (!s || !e) return dataAll;

    const lo = Math.min(s, e);
    const hi = Math.max(s, e);

    return dataAll.filter((x) => x.ts >= lo && x.ts <= hi);
  }, [dataAll, startISO, endISO]);

  const chartData = useMemo(() => buildMovingAvg(filtered, 7), [filtered]);

  const metrics = useMemo(() => {
    const total = sum(filtered, "compteur");
    const days = diffDaysInclusive(startISO, endISO);
    const avgPerDay = days ? total / days : 0;

    const todayISO = toISODateLocal(new Date());
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayISO = toISODateLocal(yesterday);

    const todayCount =
      dataAll.find((x) => x.date_visite === todayISO)?.compteur || 0;
    const yesterdayCount =
      dataAll.find((x) => x.date_visite === yesterdayISO)?.compteur || 0;

    const peak = filtered.reduce(
      (acc, x) => (x.compteur > (acc?.compteur ?? -1) ? x : acc),
      null
    );

    const lastKnownISO =
      dataAll.length > 0
        ? dataAll[dataAll.length - 1].date_visite
        : toISODateLocal(new Date());

    const lastKnownDate = parseDateOnlyLocal(lastKnownISO) || new Date();

    const makeRange = (daysBack) => {
      const e = new Date(lastKnownDate);
      const s = new Date(lastKnownDate);
      s.setDate(s.getDate() - (daysBack - 1));

      const sISO = toISODateLocal(s);
      const eISO = toISODateLocal(e);

      const arr = dataAll.filter((x) => {
        const ts = x.ts;
        return (
          ts >= parseDateOnlyLocal(sISO).getTime() &&
          ts <= parseDateOnlyLocal(eISO).getTime()
        );
      });

      const t = sum(arr, "compteur");
      return {
        total: t,
        avg: daysBack ? t / daysBack : 0,
      };
    };

    const last7 = makeRange(7);

    let deltaPct = null;
    if (days > 0 && startISO && endISO) {
      const sD = parseDateOnlyLocal(startISO);
      const prevEndD = new Date(sD);
      prevEndD.setDate(prevEndD.getDate() - 1);

      const prevStartD = new Date(prevEndD);
      prevStartD.setDate(prevStartD.getDate() - (days - 1));

      const prevStartISO = toISODateLocal(prevStartD);
      const prevEndISO = toISODateLocal(prevEndD);

      const prev = dataAll.filter((x) => {
        const ts = x.ts;
        return (
          ts >= parseDateOnlyLocal(prevStartISO).getTime() &&
          ts <= parseDateOnlyLocal(prevEndISO).getTime()
        );
      });

      const prevTotal = sum(prev, "compteur");

      if (prevTotal > 0) {
        deltaPct = ((total - prevTotal) / prevTotal) * 100;
      } else if (total > 0) {
        deltaPct = 100;
      } else {
        deltaPct = 0;
      }
    }

    return {
      todayCount,
      yesterdayCount,
      total,
      days,
      avgPerDay,
      peak,
      last7,
      deltaPct,
    };
  }, [filtered, startISO, endISO, dataAll]);

  const openSlide = () => {
    setPanelMounted(true);
    requestAnimationFrame(() => setPanelOpen(true));
  };

  const closeSlide = () => {
    setPanelOpen(false);
    setTimeout(() => setPanelMounted(false), TRANSITION_MS);
  };

  const handleDelete = async () => {
    setCleanErr("");

    if (!cleanStart || !cleanEnd) {
      setCleanErr("Veuillez sélectionner les dates de début et de fin.");
      return;
    }

    if (parseDateOnlyLocal(cleanStart) > parseDateOnlyLocal(cleanEnd)) {
      setCleanErr("La date de début doit être antérieure à la date de fin.");
      return;
    }

    setCleaning(true);
    try {
      await deleteVisitesByDate(cleanStart, cleanEnd);
      await fetchVisites();
      setCleanStart("");
      setCleanEnd("");
      closeSlide();
    } catch (e) {
      setCleanErr("Une erreur est survenue lors de la suppression.");
    } finally {
      setCleaning(false);
    }
  };

  const RangeChip = ({ k, label }) => {
    const active = rangeKey === k;

    return (
      <button
        type="button"
        onClick={() => applyQuickRange(k)}
        className={cn(
          "inline-flex items-center rounded-2xl px-3 py-2 text-sm font-semibold ring-1 transition",
          active
            ? "bg-slate-900 text-white ring-slate-900 dark:bg-white dark:text-slate-900 dark:ring-white"
            : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 ring-slate-200 dark:ring-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
        )}
      >
        {label}
      </button>
    );
  };

  const DeltaBadge = () => {
    if (metrics.deltaPct === null || Number.isNaN(metrics.deltaPct)) return null;

    const up = metrics.deltaPct >= 0;
    const Icon = up ? FaArrowUp : FaArrowDown;

    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold",
          up
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
            : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200"
        )}
      >
        <Icon className="h-3 w-3" />
        {Math.abs(metrics.deltaPct).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 backdrop-blur p-4 sm:p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xl font-extrabold text-slate-900 dark:text-white">
              Dashboard des visites
            </div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Vue rapide sur les visites du site, avec filtre par période et nettoyage des données.
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={fetchVisites}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900 transition"
              title="Rafraîchir"
            >
              <FaSyncAlt />
              Sync
            </button>

            <button
              type="button"
              onClick={openSlide}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition shadow-sm"
              title="Supprimer des visites entre deux dates"
            >
              <FaTrash />
              Nettoyer
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <div className="flex gap-2 flex-wrap">
            <RangeChip k="7d" label="7 jours" />
            <RangeChip k="30d" label="30 jours" />
            <RangeChip k="90d" label="90 jours" />
            <RangeChip k="1y" label="1 an" />
            <RangeChip k="all" label="Tout" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_auto] gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Début
              </label>
              <input
                type="date"
                value={startISO}
                onChange={(e) => {
                  setStartISO(e.target.value);
                  setRangeKey("custom");
                }}
                className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Fin
              </label>
              <input
                type="date"
                value={endISO}
                onChange={(e) => {
                  setEndISO(e.target.value);
                  setRangeKey("custom");
                }}
                className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Période
              </div>
              <div className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
                {startISO ? fmtShort(startISO) : "—"} → {endISO ? fmtShort(endISO) : "—"}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <span>{metrics.days || 0} jours</span>
                <DeltaBadge />
              </div>
            </div>
          </div>

          {err && (
            <div className="text-sm font-semibold text-rose-600 dark:text-rose-300">
              {err}
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard
          title="Aujourd'hui"
          value={metrics.todayCount}
          sub={`Visites du ${fmtLong(toISODateLocal(new Date()))}`}
          accent="indigo"
          highlight
        />
        <KpiCard
          title="Hier"
          value={metrics.yesterdayCount}
          sub="Comparaison rapide"
          accent="slate"
        />
        <KpiCard
          title="Total période"
          value={metrics.total}
          sub={`${metrics.days || 0} jours filtrés`}
          accent="emerald"
        />
        <KpiCard
          title="Moyenne / jour"
          value={metrics.avgPerDay ? metrics.avgPerDay.toFixed(2) : "0.00"}
          sub={`7 derniers jours : ${metrics.last7.avg.toFixed(2)}/jour`}
          accent="amber"
        />
      </div>

      {/* Graphique */}
      <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 backdrop-blur p-4 sm:p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-base font-extrabold text-slate-900 dark:text-white">
              Évolution des visites
            </div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Courbe bleue = visites / jour • courbe orange = moyenne glissante sur 7 jours
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 font-semibold text-slate-700 dark:text-slate-200">
              Points affichés : {filtered.length}
            </span>
            <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 font-semibold text-slate-700 dark:text-slate-200">
              Pic :{" "}
              {metrics.peak
                ? `${metrics.peak.compteur} le ${fmtShort(metrics.peak.date_visite)}`
                : "—"}
            </span>
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 0 }}>
          <ResponsiveContainer width="100%" height={430}>
            <LineChart
              data={chartData}
              margin={{ top: 12, right: 12, left: -10, bottom: 4 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(100,116,139,0.18)"
              />
              <XAxis
                dataKey="date_visite"
                tickFormatter={fmtShort}
                stroke="rgba(71,85,105,0.8)"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                stroke="rgba(71,85,105,0.8)"
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.98)",
                  border: "1px solid rgba(203,213,225,0.7)",
                  borderRadius: 14,
                  boxShadow: "0 12px 30px rgba(2,6,23,0.10)",
                }}
                labelFormatter={fmtLong}
                formatter={(value, name) => {
                  if (name === "avg7") {
                    return [Number(value).toFixed(2), "Moyenne 7 jours"];
                  }
                  return [value, "Visites"];
                }}
              />
              <Line
                type="monotone"
                dataKey="compteur"
                name="Visites"
                stroke="#6366F1"
                strokeWidth={3}
                dot={{ r: 2 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="avg7"
                name="avg7"
                stroke="#F59E0B"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Slide-over nettoyage */}
      {panelMounted && (
        <>
          <div
            className={cn(
              "fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300",
              panelOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={() => !cleaning && closeSlide()}
          />

          <div
            className={cn(
              "fixed right-0 top-0 h-full w-full max-w-sm z-[61] transform transition-transform duration-300",
              panelOpen ? "translate-x-0" : "translate-x-full"
            )}
            style={{ willChange: "transform" }}
            aria-hidden={!panelOpen}
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-950 ring-1 ring-slate-200/70 dark:ring-slate-800 shadow-2xl">
              <div className="px-4 py-3 sm:px-5 flex items-center justify-between bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white">
                <div>
                  <div className="text-sm font-extrabold">Nettoyer les visites</div>
                  <div className="text-xs/5 opacity-90">
                    Supprimer une plage de dates
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => !cleaning && closeSlide()}
                  className="h-8 w-8 grid place-items-center rounded-xl bg-white/20 hover:bg-white/30 transition"
                  title="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-4 sm:p-5 space-y-4">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Date de début
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    value={cleanStart}
                    onChange={(e) => setCleanStart(e.target.value)}
                    disabled={cleaning}
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    value={cleanEnd}
                    onChange={(e) => setCleanEnd(e.target.value)}
                    disabled={cleaning}
                  />
                </div>

                <div className="rounded-2xl bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/40 p-3 text-xs text-rose-700 dark:text-rose-200">
                  Les dates sont inclusives : toutes les visites entre le début et la fin seront supprimées.
                </div>

                {cleanErr && (
                  <div className="text-sm font-semibold text-rose-600 dark:text-rose-300">
                    {cleanErr}
                  </div>
                )}

                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ring-1 ring-slate-200/70 dark:ring-slate-800 bg-white dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900 transition"
                    onClick={closeSlide}
                    disabled={cleaning}
                  >
                    Annuler
                  </button>

                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={cleaning}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-extrabold text-white shadow-sm transition",
                      cleaning
                        ? "bg-rose-400 cursor-not-allowed"
                        : "bg-rose-600 hover:bg-rose-700"
                    )}
                  >
                    {cleaning ? (
                      <>
                        <span className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        Suppression...
                      </>
                    ) : (
                      <>
                        <FaTrash />
                        Supprimer
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="px-4 pb-4 text-xs text-slate-500 dark:text-slate-400">
                Pense à rafraîchir si tu veux recontrôler la période affichée après nettoyage.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ title, value, sub, accent = "slate", highlight = false }) {
  const accents = {
    indigo:
      "from-indigo-500/10 to-violet-500/10 border-indigo-200/60 dark:border-indigo-900/50",
    emerald:
      "from-emerald-500/10 to-green-500/10 border-emerald-200/60 dark:border-emerald-900/50",
    amber:
      "from-amber-500/10 to-orange-500/10 border-amber-200/60 dark:border-amber-900/50",
    slate:
      "from-slate-500/5 to-slate-500/10 border-slate-200/70 dark:border-slate-800",
  };

  return (
    <div
      className={cn(
        "rounded-3xl border bg-gradient-to-br p-4 shadow-sm",
        "bg-white/90 dark:bg-slate-900/50 backdrop-blur",
        accents[accent] || accents.slate,
        highlight && "ring-2 ring-indigo-200 dark:ring-indigo-900/50"
      )}
    >
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {title}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-white">
        {value}
      </div>
      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{sub}</div>
    </div>
  );
}